import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Power, Trash2, RefreshCw } from 'lucide-react';

import { useTranslation } from '../../i18n';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import {
  useBotDetail,
  useBotUpdates,
  useRedeliverBotUpdate,
  useDisableBot,
  useEnableBot,
  useDeleteBot,
} from '../../hooks/queries/useBotsQueries';
import type { BotUpdateStatus } from '../../types/bots';
import { formatDateTime } from '../../utils/formatDate';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import BotStatusBadge from './BotStatusBadge';
import BotWebhookBadge from './BotWebhookBadge';

type DetailTab = 'profile' | 'webhook' | 'domains' | 'tokens' | 'updates';
type UpdateFilter = 'all' | BotUpdateStatus;

const UPDATE_FILTERS: UpdateFilter[] = [
  'all',
  'pending',
  'delivered',
  'failed',
  'dropped',
];

const LAST_ERROR_TRUNCATE_LENGTH = 60;

const truncate = (value: string, length: number) =>
  value.length > length ? `${value.slice(0, length)}…` : value;

const BotDetailView = () => {
  const { botId } = useParams<{ botId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [tab, setTab] = useState<DetailTab>('profile');
  const [updateFilter, setUpdateFilter] = useState<UpdateFilter>('all');

  const { data: bot, isLoading } = useBotDetail(botId || '');
  const { data: updates, isLoading: updatesLoading } = useBotUpdates(
    botId || '',
    { status: updateFilter === 'all' ? undefined : updateFilter },
  );

  const disableBot = useDisableBot();
  const enableBot = useEnableBot();
  const deleteBot = useDeleteBot();
  const redeliverUpdate = useRedeliverBotUpdate();

  const confirmDialog = useConfirmDialog();

  const handleBack = () => navigate('/bots');

  if (isLoading) {
    return (
      <div className="p-4 lg:p-8 text-muted-foreground">
        {t('common.loading')}
      </div>
    );
  }

  if (!bot) {
    return (
      <div className="p-4 lg:p-8 text-muted-foreground">
        {t('common.no_data')}
      </div>
    );
  }

  const isActive = bot.status === 'active';
  const tokens = bot.tokens ?? [];

  const handleToggleEnabled = () => {
    confirmDialog.show(
      isActive ? t('bots.confirm.disable') : t('bots.confirm.enable'),
      async () => {
        if (isActive) {
          await disableBot.mutateAsync(bot.id);
        } else {
          await enableBot.mutateAsync(bot.id);
        }
      },
    );
  };

  const handleDelete = () => {
    confirmDialog.show(t('bots.confirm.delete'), async () => {
      await deleteBot.mutateAsync(bot.id);
      navigate('/bots');
    });
  };

  const handleRedeliver = (updateId: string) => {
    redeliverUpdate.mutate({ botId: bot.id, updateId });
  };

  const tabs: { key: DetailTab; label: string }[] = [
    { key: 'profile', label: t('bots.detail.profile') },
    { key: 'webhook', label: t('bots.detail.webhook') },
    { key: 'domains', label: t('bots.detail.domains') },
    { key: 'tokens', label: t('bots.detail.tokens') },
    { key: 'updates', label: t('bots.detail.updates') },
  ];

  return (
    <div className="p-4 lg:p-8 flex flex-col gap-6">
      <Button
        variant="ghost"
        onClick={handleBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-fit"
      >
        <ArrowLeft className="w-4 h-4" /> {t('bots.detail.backToBots')}
      </Button>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-linear-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-2xl overflow-hidden shrink-0">
            {bot.displayName.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold text-foreground">
                {bot.displayName}
              </h1>
              <BotStatusBadge status={bot.status} />
              <BotWebhookBadge bot={bot} />
            </div>
            <div className="flex items-center gap-2 mt-2 text-muted-foreground">
              <span>@{bot.username}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={handleToggleEnabled}
            className={`flex items-center gap-2 ${isActive ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}`}
          >
            <Power className="w-4 h-4" />
            {isActive ? t('bots.actions.disable') : t('bots.actions.enable')}
          </Button>
          <Button
            variant="outline"
            onClick={handleDelete}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            {t('bots.actions.delete')}
          </Button>
        </div>
      </div>

      {/* Tabs + content */}
      <Card>
        <CardContent className="p-6">
          <div className="inline-flex items-center rounded-lg bg-muted p-1 w-full mb-6 flex-wrap">
            {tabs.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-all ${
                  tab === key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'profile' && (
            <div className="space-y-0">
              <Field label={t('common.description')}>
                {bot.description || '—'}
              </Field>
              <Field label={t('common.name')}>
                {bot.shortDescription || '—'}
              </Field>
              <Field label={t('bots.detail.space')}>
                <span title={bot.spaceId}>
                  {bot.space?.name ?? bot.spaceId}
                </span>
              </Field>
              <Field label={t('bots.detail.owner')}>
                {bot.owner ? (
                  <div title={bot.ownerUserId}>
                    <div>{bot.owner.displayName}</div>
                    <div className="text-xs text-muted-foreground">
                      @{bot.owner.username}
                    </div>
                  </div>
                ) : (
                  <span title={bot.ownerUserId}>{bot.ownerUserId}</span>
                )}
              </Field>
              <Field label={t('bots.detail.minClientVersion')}>
                {bot.minClientVersion || '—'}
              </Field>
              <Field label={t('common.created_at')}>
                {formatDateTime(bot.createdAt)}
              </Field>
              <Field label={t('bots.detail.updatedAt')} last>
                {formatDateTime(bot.updatedAt)}
              </Field>
            </div>
          )}

          {tab === 'webhook' && (
            <div className="space-y-0">
              <Field label="URL">
                {bot.webhookUrl || t('bots.webhook.polling')}
              </Field>
              <Field label={t('bots.detail.disabledAt')}>
                {formatDateTime(bot.webhookDisabledAt)}
              </Field>
              <Field label={t('bots.detail.failStreak')}>
                {bot.webhookFailStreak}
              </Field>
              <Field label={t('bots.detail.lastError')} last>
                {bot.webhookLastError ? (
                  <pre className="whitespace-pre-wrap wrap-break-word font-mono text-xs text-foreground">
                    {bot.webhookLastError}
                  </pre>
                ) : (
                  '—'
                )}
              </Field>
            </div>
          )}

          {tab === 'domains' && (
            <div className="flex flex-col gap-6">
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  {t('bots.detail.domains')}
                </div>
                {bot.allowedDomains.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {bot.allowedDomains.map((domain) => (
                      <Badge key={domain} variant="outline">
                        {domain}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    {t('bots.detail.noDomains')}
                  </span>
                )}
              </div>

              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  {t('bots.detail.allowedUpdates')}
                </div>
                {bot.allowedUpdates === null ? (
                  <Badge variant="outline">{t('common.all')}</Badge>
                ) : bot.allowedUpdates.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {bot.allowedUpdates.map((update) => (
                      <Badge key={update} variant="outline">
                        {update}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    {t('bots.detail.noAllowedUpdates')}
                  </span>
                )}
              </div>

              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  {t('bots.detail.commands')}
                </div>
                {bot.commands.length > 0 ? (
                  <div className="overflow-hidden rounded-lg border border-border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>
                            {t('bots.detail.commandColumn')}
                          </TableHead>
                          <TableHead>{t('common.description')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bot.commands.map((command) => (
                          <TableRow key={command.command}>
                            <TableCell className="font-mono text-sm">
                              /{command.command}
                            </TableCell>
                            <TableCell className="text-sm">
                              {command.description}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    {t('bots.detail.noCommands')}
                  </span>
                )}
              </div>
            </div>
          )}

          {tab === 'tokens' && (
            <div>
              {tokens.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('bots.tokens.prefix')}</TableHead>
                        <TableHead>{t('bots.tokens.scopes')}</TableHead>
                        <TableHead>{t('bots.tokens.expires')}</TableHead>
                        <TableHead>{t('bots.tokens.lastUsed')}</TableHead>
                        <TableHead>{t('common.status')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tokens.map((token) => (
                        <TableRow key={token.id}>
                          <TableCell className="font-mono text-sm">
                            {token.tokenPrefix}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {token.scopes.map((scope) => (
                                <Badge key={scope} variant="outline">
                                  {scope}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDateTime(token.expiresAt)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDateTime(token.lastUsedAt)}
                          </TableCell>
                          <TableCell>
                            {token.revokedAt ? (
                              <Badge variant="destructive">
                                {t('bots.tokens.revoked')}
                              </Badge>
                            ) : (
                              <Badge variant="success">
                                {t('common.active')}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {t('bots.detail.noTokens')}
                </span>
              )}
            </div>
          )}

          {tab === 'updates' && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-2">
                {UPDATE_FILTERS.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setUpdateFilter(filter)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      updateFilter === filter
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {filter === 'all'
                      ? t('common.all')
                      : t(`bots.updates.status.${filter}`)}
                  </button>
                ))}
              </div>

              {updatesLoading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">
                  {t('common.loading')}
                </div>
              ) : !updates || updates.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center">
                  {t('bots.detail.noUpdates')}
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('bots.updates.id')}</TableHead>
                        <TableHead>{t('bots.updates.type')}</TableHead>
                        <TableHead>{t('bots.updates.status')}</TableHead>
                        <TableHead>{t('bots.updates.attempts')}</TableHead>
                        <TableHead>{t('bots.updates.lastError')}</TableHead>
                        <TableHead>{t('bots.updates.created')}</TableHead>
                        <TableHead>{t('bots.updates.delivered')}</TableHead>
                        <TableHead className="text-right">
                          {t('common.actions')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {updates.map((update) => {
                        const canRedeliver =
                          update.status === 'failed' ||
                          update.status === 'dropped';
                        const isRedelivering =
                          redeliverUpdate.isPending &&
                          redeliverUpdate.variables?.updateId === update.id;

                        return (
                          <TableRow key={update.id}>
                            <TableCell className="text-sm">
                              {update.id}
                            </TableCell>
                            <TableCell className="text-sm">
                              {update.type}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  update.status === 'delivered'
                                    ? 'success'
                                    : update.status === 'pending'
                                      ? 'info'
                                      : update.status === 'failed'
                                        ? 'destructive'
                                        : 'muted'
                                }
                              >
                                {t(`bots.updates.status.${update.status}`)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                              {update.attempts}
                            </TableCell>
                            <TableCell
                              className="text-sm text-muted-foreground max-w-50 truncate"
                              title={update.lastError || undefined}
                            >
                              {update.lastError
                                ? truncate(
                                    update.lastError,
                                    LAST_ERROR_TRUNCATE_LENGTH,
                                  )
                                : '—'}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                              {formatDateTime(update.createdAt)}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                              {formatDateTime(update.deliveredAt)}
                            </TableCell>
                            <TableCell className="text-right">
                              {canRedeliver && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={isRedelivering}
                                  onClick={() => handleRedeliver(update.id)}
                                  className="gap-1.5"
                                >
                                  <RefreshCw className="w-3.5 h-3.5" />
                                  {t('bots.actions.redeliver')}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm dialog (disable / enable / delete) */}
      <Dialog
        open={confirmDialog.isOpen}
        onOpenChange={(open) => !open && confirmDialog.cancel()}
      >
        <DialogContent className="sm:max-w-105">
          <DialogHeader>
            <DialogTitle>{t('common.confirm')}</DialogTitle>
            <DialogDescription>{confirmDialog.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={confirmDialog.cancel}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDialog.confirm}
              disabled={confirmDialog.isLoading}
            >
              {confirmDialog.isLoading
                ? t('common.saving')
                : t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({
  label,
  children,
  last = false,
}: {
  label: string;
  children: React.ReactNode;
  last?: boolean;
}) => (
  <div
    className={`flex items-start gap-3 py-3 ${last ? '' : 'border-b border-border'}`}
  >
    <div className="flex-1">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="text-sm text-foreground mt-1">{children}</div>
    </div>
  </div>
);

export default BotDetailView;
